import NextContentfulImage from "@components/Image/NextContentfulImage";
import clsx from "clsx";
import dynamic from "next/dynamic";
import { memo, useCallback, useState } from "react";
import stopIcon from "../../public/image/stopvideo.png";
import videoIcon from "../../public/image/vidicon.png";
import styles from "./C46_HeaderBanner.module.scss";
import { HeaderVideoPlayerProps } from "./C46_HeaderVideoPlayer.interfaces";

const VideoPlayer = dynamic(() => import("@components/VideoPlayer"), {
  ssr: false,
});

function C46_HeaderVideoPlayer({
  url,
  onVideoReady,
  onVideoPlay,
  onVideoEnded,
  videoDescription = "This video is playing in the background and is muted",
}: HeaderVideoPlayerProps) {
  const [isPlaying, setIsPlaying] = useState(true);

  const handleVideoPlay = useCallback(() => {
    setIsPlaying(true);
    setTimeout(onVideoPlay, 300);
  }, [onVideoPlay]);

  const handleVideoReady = useCallback(() => {
    setTimeout(onVideoReady, 300);
  }, [onVideoReady]);

  const handleVideoEnded = useCallback(() => {
    setIsPlaying(false);
    onVideoEnded();
  }, [onVideoEnded]);

  return (
    <>
      <button
        type="button"
        onClick={isPlaying ? handleVideoEnded : handleVideoPlay}
        className={clsx(styles["video-control-icon"])}
        aria-label={isPlaying ? "Stop video" : "Play video"}
        aria-controls="background-video"
      >
        <NextContentfulImage
          src={isPlaying ? stopIcon : videoIcon}
          alt=""
          width={54}
          height={54}
        />
      </button>

      <div
        id="background-video"
        aria-labelledby="video-description"
        // Ensure the video is discoverable by assistive technologies but not focusable
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex={isPlaying ? 0 : -1} // Allow focus only when video is playing
      >
        <div aria-hidden="true">
          <VideoPlayer
            className={styles["video-player"]}
            url={url}
            playing={isPlaying}
            muted
            onReady={handleVideoReady}
            onEnded={handleVideoEnded}
          />
        </div>
      </div>
      <p id="video-description" className="sr-only">
        {videoDescription}
      </p>
    </>
  );
}

export default memo(C46_HeaderVideoPlayer);
